import React, { useState } from 'react';
import { postFeedback } from '../../api/client';

/**
 * Reusable feedback popup.
 *
 * Used in two places:
 *  • Chat: opened when a user clicks 👎 on a DASH answer (queryId is supplied
 *    so admin can join the comment back to the original question).
 *  • Footer "Feedback" pill: standalone "tell us anything" capture — no queryId.
 *
 * Submits to POST /api/feedback as rating='down' (the only existing rating
 * column that holds free-form comments).  The admin Feedback tab shows the
 * full contact details + comment regardless of source.
 */
export default function FeedbackModal({
  queryId = null,
  onClose,
  onSubmitted,
  title = 'Help us improve DASH',
  subtitle = 'Tell us what went wrong with this answer. Contact info is optional.',
  rating = 'down',
}) {
  const [firstName, setFirstName] = useState('');
  const [lastName,  setLastName]  = useState('');
  const [email,     setEmail]     = useState('');
  const [comment,   setComment]   = useState('');
  const [sending,   setSending]   = useState(false);
  const [err,       setErr]       = useState('');
  const [done,      setDone]      = useState(false);

  const validate = () => {
    if (!comment.trim()) return 'Please share a few words of feedback.';
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) return 'Enter a valid email.';
    return '';
  };

  const submit = async () => {
    const v = validate();
    if (v) { setErr(v); return; }
    setErr('');
    setSending(true);
    try {
      await postFeedback(queryId, rating, comment.trim(), {
        first_name: firstName.trim() || null,
        last_name:  lastName.trim()  || null,
        email:      email.trim()     || null,
      });
      setDone(true);
      onSubmitted?.();
    } catch (e) {
      setErr('Could not send. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      onClick={onClose}
      className="dash-fb-modal"
      style={{
        position: 'fixed', inset: 0, zIndex: 3000,
        background: 'rgba(15,23,42,0.45)', backdropFilter: 'blur(3px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
        animation: 'fbFadeIn 0.16s ease',
      }}
    >
      <style>{`
        @keyframes fbFadeIn { from { opacity: 0; } to { opacity: 1; } }
        /* Softer placeholder text inside the feedback popup only */
        .dash-fb-modal input::placeholder,
        .dash-fb-modal textarea::placeholder { color: #cbd5e1; opacity: 1; font-weight: 400; }
      `}</style>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(440px, 100%)',
          background: '#fff',
          borderRadius: 16,
          boxShadow: '0 30px 60px rgba(13,79,82,0.30)',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '16px 20px',
          background: 'linear-gradient(135deg, #0d4f52, #1d9c9c)',
          color: '#fff',
        }}>
          <div style={{ fontSize: 15, fontWeight: 800, letterSpacing: '-0.01em' }}>{title}</div>
          <div style={{ fontSize: 12, opacity: 0.85, marginTop: 3 }}>{subtitle}</div>
        </div>

        {done ? (
          <div style={{ padding: '30px 20px', textAlign: 'center' }}>
            <div style={{ fontSize: 36, marginBottom: 10 }}>✅</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>
              Thanks for the feedback!
            </div>
            <div style={{ fontSize: 13, color: '#64748b', marginBottom: 18 }}>
              We'll review it and improve DASH.
            </div>
            <button
              onClick={onClose}
              style={{
                padding: '9px 26px',
                background: 'linear-gradient(135deg, #0d4f52, #1d9c9c)',
                color: '#fff', border: 'none',
                borderRadius: 'var(--radius-btn, 10px)',
                fontSize: 13.5, fontWeight: 700, cursor: 'pointer',
              }}
            >Close</button>
          </div>
        ) : (
          <>
            {/* Body */}
            <div style={{ padding: '18px 20px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
                <Field label="First name">
                  <input value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="Jane" style={inp} />
                </Field>
                <Field label="Last name">
                  <input value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Doe" style={inp} />
                </Field>
              </div>

              <Field label="Email">
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  style={inp} />
              </Field>

              <Field label="Your feedback" required>
                <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={5}
                  placeholder="What went wrong, what should we add, or anything else…"
                  style={{ ...inp, resize: 'vertical', fontFamily: 'inherit' }} />
              </Field>

              {err && (
                <div style={{
                  padding: '8px 12px', marginTop: 6,
                  background: '#fff5f5', border: '1px solid #feb2b2',
                  borderRadius: 8, color: '#c53030', fontSize: 12.5,
                }}>{err}</div>
              )}
            </div>

            {/* Footer */}
            <div style={{
              padding: '12px 20px', borderTop: '1px solid #e2e8f0',
              display: 'flex', justifyContent: 'flex-end', gap: 8,
            }}>
              <button onClick={onClose} disabled={sending} style={{
                padding: '8px 16px', border: '1px solid #cbd5e1', background: '#fff',
                borderRadius: 'var(--radius-btn, 10px)', color: '#475569',
                fontSize: 13.5, fontWeight: 600, cursor: 'pointer',
              }}>Cancel</button>
              <button onClick={submit} disabled={sending || !comment.trim()} style={{
                padding: '8px 20px',
                background: 'linear-gradient(135deg, #0d4f52, #1d9c9c)',
                color: '#fff', border: 'none',
                borderRadius: 'var(--radius-btn, 10px)',
                fontSize: 13.5, fontWeight: 700,
                cursor: (sending || !comment.trim()) ? 'not-allowed' : 'pointer',
                opacity: (sending || !comment.trim()) ? 0.55 : 1,
              }}>{sending ? 'Sending…' : 'Send feedback'}</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const inp = {
  width: '100%', padding: '8px 11px',
  border: '1px solid #cbd5e1', borderRadius: 8,
  fontSize: 13.5, outline: 'none', boxSizing: 'border-box',
  background: '#fff', color: '#0f172a',
};
function Field({ label, required, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{
        fontSize: 11, fontWeight: 700, color: '#475569',
        textTransform: 'uppercase', letterSpacing: '0.06em',
        marginBottom: 5,
      }}>
        {label}{required && <span style={{ color: '#c53030', marginLeft: 4 }}>*</span>}
      </div>
      {children}
    </div>
  );
}
